import { useFormContext } from "react-hook-form";
import { AddressFields } from "@/components/AddressFields";
import { formatPhone } from "@/lib/formatters";
import { BILLING_ADDRESS_DESCRIPTION } from "@/lib/checkout-form";
import type { CheckoutFormData } from "@/pages/CheckoutPage";

export function CompanyInfoFields(): React.ReactElement {
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<CheckoutFormData>();

  const mailingAddressSameAsBilling = watch("mailingAddressSameAsBilling") as
    | boolean
    | undefined;

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">
        Company Information
      </h3>

      <div>
        <label
          htmlFor="companyName"
          className="block text-sm font-medium text-gray-700"
        >
          Company Name
        </label>
        <input
          id="companyName"
          type="text"
          {...register("companyName")}
          className={`mt-1 block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            errors.companyName?.message
              ? "border-red-500 focus:ring-red-500"
              : "border-gray-300"
          }`}
        />
        {errors.companyName?.message && (
          <p className="mt-1 text-xs text-red-600">
            {errors.companyName?.message}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label
            htmlFor="contactFirstName"
            className="block text-sm font-medium text-gray-700"
          >
            Contact First Name
          </label>
          <input
            id="contactFirstName"
            type="text"
            autoComplete="given-name"
            {...register("contactFirstName")}
            className={`mt-1 block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.contactFirstName?.message
                ? "border-red-500 focus:ring-red-500"
                : "border-gray-300"
            }`}
          />
          {errors.contactFirstName?.message && (
            <p className="mt-1 text-xs text-red-600">
              {errors.contactFirstName?.message}
            </p>
          )}
        </div>
        <div>
          <label
            htmlFor="contactLastName"
            className="block text-sm font-medium text-gray-700"
          >
            Contact Last Name
          </label>
          <input
            id="contactLastName"
            type="text"
            autoComplete="family-name"
            {...register("contactLastName")}
            className={`mt-1 block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.contactLastName?.message
                ? "border-red-500 focus:ring-red-500"
                : "border-gray-300"
            }`}
          />
          {errors.contactLastName?.message && (
            <p className="mt-1 text-xs text-red-600">
              {errors.contactLastName?.message}
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label
            htmlFor="contactPhone"
            className="block text-sm font-medium text-gray-700"
          >
            Contact Phone
          </label>
          <input
            id="contactPhone"
            type="tel"
            autoComplete="tel"
            placeholder="(850) 555-1212"
            value={(watch("contactPhone") as string) ?? ""}
            onChange={(e) => {
              setValue("contactPhone", formatPhone(e.target.value), { shouldValidate: true });
            }}
            className={`mt-1 block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.contactPhone?.message
                ? "border-red-500 focus:ring-red-500"
                : "border-gray-300"
            }`}
          />
          {errors.contactPhone?.message && (
            <p className="mt-1 text-xs text-red-600">
              {errors.contactPhone?.message}
            </p>
          )}
        </div>
        <div>
          <label
            htmlFor="contactEmail"
            className="block text-sm font-medium text-gray-700"
          >
            Contact Email
          </label>
          <input
            id="contactEmail"
            type="email"
            autoComplete="email"
            {...register("contactEmail")}
            className={`mt-1 block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.contactEmail?.message
                ? "border-red-500 focus:ring-red-500"
                : "border-gray-300"
            }`}
          />
          {errors.contactEmail?.message && (
            <p className="mt-1 text-xs text-red-600">
              {errors.contactEmail?.message}
            </p>
          )}
        </div>
      </div>

      <AddressFields
        prefix="billingAddress"
        label="Billing Address"
        description={BILLING_ADDRESS_DESCRIPTION}
      />

      <div className="flex items-center gap-2">
        <input
          id="mailingAddressSameAsBilling"
          type="checkbox"
          {...register("mailingAddressSameAsBilling")}
          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
        <label
          htmlFor="mailingAddressSameAsBilling"
          className="text-sm text-gray-700"
        >
          Mailing address same as billing
        </label>
      </div>

      {mailingAddressSameAsBilling === false && (
        <AddressFields prefix="mailingAddress" label="Mailing Address" />
      )}
    </div>
  );
}
